import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Row, Col, Card, CardBody, Button, Input, Spinner, Alert,
    Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API } from "../../api/urls";
import { fetchMySupportBadge } from "../../store/supportBadge/actions";
import PaginationBar from "../../components/Common/PaginationBar";
import NewTicketModal from "./NewTicketModal";
import { ticketListStyles, TICKET_NAVY } from "./ticketListStyles";
import {
    StatusPill, PriorityPill, CATEGORY_META, CATEGORY_OPTIONS, PRIORITY_META, PRIORITY_OPTIONS,
    STATUS_OPTIONS, STATUS_META, fmtDateTime, formatTicketNo,
} from "./ticketMeta";
import { useAuth } from "../../hooks/useAuth";

const NAVY = TICKET_NAVY;

/**
 * Satır işlemleri — müşteri yüzeyi. Destek Yönetimi'ndeki menünün karşılığıdır ama
 * atama ve durum değiştirme YOKTUR: ikisi de personel yetkisidir (TicketAdminController).
 * Kullanıcının kendi talebinde alabileceği aksiyon talebi kapatmaktır.
 */
const RowActions = ({ ticket, busy, onOpen, onClose }) => {
    const [open, setOpen] = useState(false);
    return (
        <Dropdown isOpen={open} toggle={() => setOpen((o) => !o)} direction="start">
            <DropdownToggle tag="button" className="btn btn-link text-muted p-0 border-0"
                onClick={(e) => e.stopPropagation()} aria-label="İşlemler">
                <i className="bx bx-dots-vertical-rounded fs-5" />
            </DropdownToggle>
            {/* Menüdeki tıklamalar satırın kendi onClick'ini (detaya git) tetiklemesin. */}
            <DropdownMenu end onClick={(e) => e.stopPropagation()}>
                <DropdownItem onClick={onOpen}>
                    <i className="bx bx-detail me-2" />Detaya git
                </DropdownItem>
                <DropdownItem divider />
                <DropdownItem disabled={busy || ticket.status === "CLOSED"} onClick={onClose}>
                    <i className="bx bx-check-circle me-2" />Talebi kapat
                </DropdownItem>
            </DropdownMenu>
        </Dropdown>
    );
};

export default function MyTicketsPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rowBusy, setRowBusy] = useState(null);

    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [priorityFilter, setPriorityFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [sortBy, setSortBy] = useState("lastReplyAt");
    const [sortDir, setSortDir] = useState("desc");

    const [createOpen, setCreateOpen] = useState(false);

    // Personel kendi adına ve başkası adına açtığı talepleri aynı listede görür;
    // firma/kullanıcı kolonları yalnız bu rolde çizilir.
    const { roles } = useAuth();
    const isSuperAdmin = roles.includes("ROLE_SUPER_ADMIN");

    // Listeye en son hangi arama teriminin gönderildiği; debounce efekti bununla
    // karşılaştırır ki ilk açılışta ve filtre temizlemede aynı arama iki kez gitmesin.
    const appliedSearch = useRef("");
    document.title = "Destek Taleplerim | Tahsilat360";

    const fetchTickets = useCallback(async (p = 0, opts = {}) => {
        const size = opts.size ?? pageSize;
        const search = opts.search ?? searchTerm;
        const status = opts.status ?? statusFilter;
        const priority = opts.priority ?? priorityFilter;
        const category = opts.category ?? categoryFilter;
        const sBy = opts.sortBy ?? sortBy;
        const sDir = opts.sortDir ?? sortDir;
        try {
            setLoading(true); setError(null);
            const params = { page: p, size };
            if (search) params.searchTerm = search;
            if (status) params.status = status;
            if (priority) params.priority = priority;
            if (category) params.category = category;
            if (sBy) { params.sortBy = sBy; params.sortDir = sDir; }
            const { data } = await axios.get(API.tickets_list, { params });
            appliedSearch.current = search;
            const d = data.data;
            setTickets(d.content || []);
            setTotalPages(d.totalPages || 0);
            setTotalElements(d.totalElements || 0);
            setPage(d.page ?? p);
            // Menü rozeti listeyle aynı anda tazelensin; ayrı bir polling yok.
            dispatch(fetchMySupportBadge());
        } catch (err) {
            if (err.response?.status === 401) return navigate("/login");
            setError(err.response?.data?.message || "Destek talepleri yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, [pageSize, searchTerm, statusFilter, priorityFilter, categoryFilter,
        sortBy, sortDir, navigate, dispatch]);

    useEffect(() => { fetchTickets(0); /* eslint-disable-next-line */ }, []);

    // Arama debounce
    useEffect(() => {
        if (searchTerm === appliedSearch.current) return;
        const t = setTimeout(() => fetchTickets(0, { search: searchTerm }), 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [searchTerm]);

    const handleSort = (field) => {
        const nextDir = sortBy === field ? (sortDir === "asc" ? "desc" : "asc") : "asc";
        setSortBy(field); setSortDir(nextDir);
        fetchTickets(0, { sortBy: field, sortDir: nextDir });
    };
    const sortIcon = (field) =>
        sortBy === field ? (sortDir === "asc" ? "bx bx-sort-up" : "bx bx-sort-down") : "bx bx-sort-alt-2";

    const SortableTh = ({ field, children, className }) => (
        <Th className={`sort-th ${className || ""}`} onClick={() => handleSort(field)}>
            {children} <i className={sortIcon(field)} />
        </Th>
    );

    const activeFilterCount =
        [searchTerm, statusFilter, priorityFilter, categoryFilter].filter(Boolean).length;

    const clearFilters = () => {
        setSearchTerm(""); setStatusFilter(""); setPriorityFilter(""); setCategoryFilter("");
        fetchTickets(0, { search: "", status: "", priority: "", category: "" });
    };

    const handleClose = async (ticketId) => {
        setRowBusy(ticketId);
        try {
            await axios.patch(API.ticket_close(ticketId));
            toast.success("Talep kapatıldı");
            fetchTickets(page);
        } catch (err) {
            toast.error(err.response?.data?.message || "Talep kapatılamadı");
        } finally {
            setRowBusy(null);
        }
    };

    return (
        <div className="page-content tickets-modern">
            <style>{ticketListStyles}</style>
            <div className="container-fluid">

                {/* ── Başlık ─────────────────────────────────────────── */}
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                    <div>
                        <div className="mb-1" style={{ fontSize: ".82rem" }}>
                            <span style={{ color: NAVY, fontWeight: 600 }}>Destek</span>
                            <i className="bx bx-chevron-right mx-1 text-muted" />
                            <span className="text-muted">Taleplerim</span>
                        </div>
                        <h4 className="mb-1 fw-bold text-dark">Destek Taleplerim</h4>
                        <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                            Açtığınız talepleri takip edin, gelen yanıtları buradan görün.
                        </p>
                    </div>
                    <Button className="d-flex align-items-center gap-1"
                        style={{ background: NAVY, borderColor: NAVY }}
                        onClick={() => setCreateOpen(true)}>
                        <i className="bx bx-plus" /> Talep Oluştur
                    </Button>
                </div>

                <Row>
                    <Col>
                        <Card className="rounded-4 border-0 shadow-sm">
                            {/* ── Filtreler (atanan filtresi yok: personel alanı) ── */}
                            <div className="filter-bar">
                                <Input type="search" bsSize="sm" className="rounded-pill filter-search"
                                    placeholder="Konu veya talep no ara..."
                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

                                <Input type="select" bsSize="sm" className="rounded-pill filter-select"
                                    value={categoryFilter}
                                    onChange={(e) => { setCategoryFilter(e.target.value); fetchTickets(0, { category: e.target.value }); }}>
                                    <option value="">Tüm Kategoriler</option>
                                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{CATEGORY_META[c]}</option>)}
                                </Input>

                                <Input type="select" bsSize="sm" className="rounded-pill filter-select"
                                    value={priorityFilter}
                                    onChange={(e) => { setPriorityFilter(e.target.value); fetchTickets(0, { priority: e.target.value }); }}>
                                    <option value="">Tüm Öncelikler</option>
                                    {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
                                </Input>

                                <Input type="select" bsSize="sm" className="rounded-pill filter-select"
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); fetchTickets(0, { status: e.target.value }); }}>
                                    <option value="">Tüm Durumlar</option>
                                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                                </Input>

                                <Button size="sm" outline color="secondary" className="rounded-pill ms-auto"
                                    disabled={activeFilterCount === 0} onClick={clearFilters}>
                                    <i className="bx bx-refresh me-1" />Filtreleri Temizle
                                </Button>
                            </div>

                            <CardBody className="pt-3">
                                {error && <Alert color="danger" className="rounded-3">{error}</Alert>}

                                {loading ? (
                                    <div className="text-center p-5"><Spinner color="primary" /></div>
                                ) : (
                                    <>
                                        <div className="table-responsive rounded-4 border">
                                            <Table className="table table-hover mb-0">
                                                <Thead>
                                                    <Tr>
                                                        <SortableTh field="subject">Konu</SortableTh>
                                                        {/* Personel kendi adına ve başkası adına açtığı talepleri
                                                            aynı listede görür; talebe girmeden ayırt edebilmeli. */}
                                                        {isSuperAdmin && (
                                                            <SortableTh field="company.companyName" className="d-none d-lg-table-cell">Firma</SortableTh>
                                                        )}
                                                        {isSuperAdmin && (
                                                            <SortableTh field="createdBy.username" className="d-none d-lg-table-cell">Kullanıcı</SortableTh>
                                                        )}
                                                        <SortableTh field="category" className="d-none d-xl-table-cell">Kategori</SortableTh>
                                                        <SortableTh field="priority">Öncelik</SortableTh>
                                                        <SortableTh field="status">Durum</SortableTh>
                                                        <SortableTh field="lastReplyAt" className="d-none d-md-table-cell">Son Yanıt</SortableTh>
                                                        <SortableTh field="createdAt" className="d-none d-lg-table-cell">Oluşturma</SortableTh>
                                                        <Th style={{ width: 48 }}>İşlemler</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {tickets.length ? tickets.map((t) => (
                                                        <Tr key={t.id} style={{ cursor: "pointer" }}
                                                            onClick={() => navigate(`/support/${t.id}`)}>
                                                            <Td>
                                                                <div className="ticket-subject">
                                                                    {t.subject}
                                                                    {t.lastReplyByStaff && t.status === "ANSWERED" && (
                                                                        <span className="ms-2 badge bg-success-subtle text-success"
                                                                            style={{ fontSize: ".62rem" }}>yeni yanıt</span>
                                                                    )}
                                                                </div>
                                                                <div className="ticket-no">#{formatTicketNo(t.ticketNo)}</div>
                                                            </Td>
                                                            {isSuperAdmin && (
                                                                <Td className="d-none d-lg-table-cell">{t.companyName || "—"}</Td>
                                                            )}
                                                            {isSuperAdmin && (
                                                                <Td className="d-none d-lg-table-cell">{t.createdByUsername || "—"}</Td>
                                                            )}
                                                            <Td className="d-none d-xl-table-cell">{CATEGORY_META[t.category] || t.category}</Td>
                                                            <Td><PriorityPill priority={t.priority} /></Td>
                                                            <Td><StatusPill status={t.status} /></Td>
                                                            <Td className="d-none d-md-table-cell">{fmtDateTime(t.lastReplyAt)}</Td>
                                                            <Td className="d-none d-lg-table-cell">{fmtDateTime(t.createdAt)}</Td>
                                                            <Td onClick={(e) => e.stopPropagation()}>
                                                                {rowBusy === t.id
                                                                    ? <Spinner size="sm" color="secondary" />
                                                                    : <RowActions
                                                                        ticket={t}
                                                                        busy={rowBusy === t.id}
                                                                        onOpen={() => navigate(`/support/${t.id}`)}
                                                                        onClose={() => handleClose(t.id)}
                                                                    />}
                                                            </Td>
                                                        </Tr>
                                                    )) : (
                                                        <Tr>
                                                            <Td colSpan={isSuperAdmin ? 9 : 7} className="text-center py-4 text-muted">
                                                                {activeFilterCount > 0
                                                                    ? "Filtrelere uyan talep bulunamadı"
                                                                    : "Henüz talebiniz yok"}
                                                            </Td>
                                                        </Tr>
                                                    )}
                                                </Tbody>
                                            </Table>
                                        </div>

                                        <PaginationBar
                                            currentPage={page}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={pageSize}
                                            onPageChange={(p) => fetchTickets(p)}
                                            onPageSizeChange={(s) => { setPageSize(s); fetchTickets(0, { size: s }); }}
                                        />
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>

            <NewTicketModal
                isOpen={createOpen}
                toggle={() => setCreateOpen(false)}
                onCreated={(newId) => (newId ? navigate(`/support/${newId}`) : fetchTickets(0))}
            />
        </div>
    );
}
